import React from 'react';
import { CreditCard, Package, ShoppingBag, CheckCircle2, Clock } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { formatNumberDE } from 'core/utils/format';

const CartSummary = ({
  cart,
  selectedItems,
  getCartTotal,
  getSelectedTotal,
  formatPrice,
  ociMode,
  ociParams,
  ociCheckout,
  createPurchaseRequisition,
  user
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const hasSelection = selectedItems && selectedItems.length > 0;

  return (
    <div className="w-full lg:w-80 flex-shrink-0 self-start">
      <div className="relative group/summary lg:sticky lg:top-4 space-y-3">

        {/* Haupt-Karte: Schätzpreis + Submit-Buttons */}
        <div className="relative">
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl'
            : 'bg-white border border-slate-200'
          } rounded-2xl shadow-sm group-hover/summary:shadow-md transition-all duration-300`}></div>

          <div className="relative p-4">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border ${
                isDarkMode
                  ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 border-white/20'
                  : 'bg-brand/10 border-brand/20'
              }`}>
                <CreditCard className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-brand'}`} />
              </div>
              <div>
                <h3 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('cart.summary')}</h3>
                <p className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>{t('cart.yourOrder')}</p>
              </div>
            </div>

            {/* Auswahl-Info-Pill */}
            {hasSelection && (
              <div className={`mb-3 px-3 py-2 rounded-lg text-xs font-medium text-center ${
                isDarkMode ? 'bg-brand/20 border border-brand/30 text-blue-300' : 'bg-brand/10 border border-brand/20 text-brand'
              }`}>
                {t('cart.selectedItems', { count: `${selectedItems.length} / ${cart.length}` })}
              </div>
            )}

            {/* Positionen-Zeile */}
            <div className={`flex justify-between items-center text-sm mb-2 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
              <span>{t('cart.items')}</span>
              <span className={`font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'}`}>
                {hasSelection ? `${selectedItems.length} / ${cart.length}` : cart.length}
              </span>
            </div>

            {/* Schätzpreis-Block — Label oben, Betrag darunter (volle Breite, kein Überlauf) */}
            <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
              <span className={`block text-sm font-medium ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                {t('cart.estimatedTotal')}
              </span>
              <div className="mt-1 text-2xl font-bold text-brand tabular-nums break-words leading-tight">
                {formatNumberDE(hasSelection ? getSelectedTotal() : getCartTotal())} EUR
              </div>
              <p className={`text-xs mt-1 leading-relaxed ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                {t('cart.estimatedHint')}
              </p>
            </div>

            {/* Submit-Buttons */}
            <div className="mt-4">
              {ociMode && ociParams ? (
                /* OCI-Mode: Übertragung an SAP — Emerald bleibt */
                <>
                  <button
                    onClick={() => ociCheckout(hasSelection ? selectedItems : null)}
                    disabled={(hasSelection ? selectedItems.length === 0 : cart.length === 0) || !ociParams.hookUrl}
                    className={`relative group/oci w-full overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:transform-none disabled:opacity-50 ${
                      isDarkMode ? '' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    }`}
                    title={!ociParams.hookUrl ? t('cart.ociHookMissingTitle') : ''}
                    style={isDarkMode ? {
                      background: 'linear-gradient(135deg, rgba(16,185,129,0.7) 0%, rgba(34,197,94,0.7) 50%, rgba(16,185,129,0.7) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 8px 32px rgba(16,185,129,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                    } : {}}
                  >
                    <div
                      className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/oci:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                      style={isDarkMode ? {
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                      } : {}}
                    ></div>
                    <div className="relative z-10 py-3 px-4 flex items-center justify-center font-medium text-white">
                      <Package className="w-5 h-5 mr-2 group-hover/oci:animate-pulse" />
                      {hasSelection ? t('cart.ociTransferSelectedBtn') : t('cart.ociTransferBtn')}
                    </div>
                    <div className={`absolute inset-0 opacity-0 group-hover/oci:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                      <div
                        className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/oci:translate-x-full transition-transform duration-800"
                        style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)' }}
                      ></div>
                    </div>
                  </button>

                  <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-emerald-600/10 border border-emerald-400/20' : 'bg-emerald-50 border border-emerald-200'}`}>
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse mr-2"></div>
                      <p className={`text-sm font-bold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                        {t('cart.ociModeActive')}
                      </p>
                    </div>
                    <p className={`text-xs text-center leading-relaxed ${isDarkMode ? 'text-emerald-300/80' : 'text-emerald-600'}`}>
                      <strong>{t('cart.ociUser')}</strong> {ociParams.username || t('cart.ociUserFallback')}<br/>
                      <strong>{t('cart.ociSystem')}</strong> {ociParams.caller || 'SAP'}<br/>
                      {ociParams.hookUrl ? (
                        <span className={`inline-block mt-1 px-2 py-1 rounded text-xs font-medium ${isDarkMode ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/30' : 'bg-emerald-100 text-emerald-700 border border-emerald-200'}`}>
                          {t('cart.ociNoApproval')}
                        </span>
                      ) : (
                        <span className={`inline-block mt-1 px-2 py-1 rounded text-xs font-medium ${isDarkMode ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-400/30' : 'bg-yellow-100 text-yellow-700 border border-yellow-300'}`}>
                          {t('cart.ociHookMissing')}
                        </span>
                      )}
                    </p>
                  </div>
                </>
              ) : (
                /* Normaler Modus: BANF-Workflow */
                <button
                  onClick={() => createPurchaseRequisition(hasSelection ? selectedItems : null)}
                  disabled={hasSelection ? selectedItems.length === 0 : cart.length === 0}
                  className={`relative group/banf w-full overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:transform-none disabled:opacity-50 ${
                    isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white'
                  }`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(37,99,235,0.8) 0%, rgba(29,78,216,0.8) 50%, rgba(37,99,235,0.8) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(37,99,235,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  } : {}}
                >
                  <div
                    className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/banf:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                    style={isDarkMode ? {
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                    } : {}}
                  ></div>
                  <div className="relative z-10 py-3 px-4 flex items-center justify-center font-medium text-white">
                    <ShoppingBag className="w-5 h-5 mr-2 transition-transform duration-300 group-hover/banf:scale-110" />
                    {hasSelection ? t('cart.createBanfSelectedBtn') : t('cart.createBanfBtn')}
                  </div>
                  <div className={`absolute inset-0 opacity-0 group-hover/banf:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                    <div
                      className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/banf:translate-x-full transition-transform duration-800"
                      style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)' }}
                    ></div>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Genehmigungsweg-Karte */}
        <div className={`relative rounded-xl border px-4 py-3 ${
          isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs font-semibold ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
              {t('cart.approvalPath')}
            </span>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className={`text-xs ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                {t('cart.approvalDeptHead')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`} />
              <span className={`text-xs ${isDarkMode ? 'text-amber-300/80' : 'text-amber-600'}`}>
                {t('cart.approvalFinance')}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CartSummary;
